import React from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import { Button, ButtonGroup } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";
import ocButtonGroupState from "oc/states/button-goup-state";

interface UseOcButtonGroupProps {
  id: string;
  buttonsDef?: string[];
}

interface UseOcButtonGroupResult {
  OcButtonGroup: React.FC;
  selectedButton: string | undefined;
  handleButtonChange: (index: number) => void;
}

export const useOcButtonGroup = ({
  id,
  buttonsDef = [],
}: UseOcButtonGroupProps): UseOcButtonGroupResult => {
  const { t } = useTranslation();
  const isMobile = useOcIsMobile();
  const theme = useTheme();

  const [selectedButton, setSelectedButton] = useRecoilState<number>(
    ocButtonGroupState(id),
  );

  const handleButtonChange = (index: number) => {
    setSelectedButton(index);
  };

  const OcButtonGroup: React.FC = () => {
    if (buttonsDef.length === 0) {
      return <div>Missing buttonsDef property</div>;
    }

    return (
      <ButtonGroup
        orientation={isMobile ? "vertical" : "horizontal"}
        fullWidth={isMobile}
      >
        {buttonsDef.map((item, index) => {
          const translatedItem = t(item);

          const isSelected = index === selectedButton;

          return (
            <Button
              id={item}
              disableElevation
              key={index}
              variant={isSelected ? "contained" : "outlined"}
              color={
                isSelected ? "primary" : (theme.palette.darkBase.main as any)
              }
              onClick={() => handleButtonChange(index)}
              sx={
                !isSelected
                  ? { border: `1px solid ${theme.palette.gray40.main}` }
                  : undefined
              }
            >
              {translatedItem}
            </Button>
          );
        })}
      </ButtonGroup>
    );
  };

  return {
    OcButtonGroup,
    selectedButton: buttonsDef[selectedButton],
    handleButtonChange,
  };
};

export default useOcButtonGroup;
